import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { cn } from '@/utils';
import { MarketRunners } from '@/store/api/runnersApi';
import { useResultModal, ResultValue } from '@/hooks/useResultModal';
import ResultConfirmModal from './ResultConfirmModal';

interface ResultModalProps {
  open: boolean;
  onClose: () => void;
  marketType?: string;
  eventName: string;
  marketName?: string;
  runners: MarketRunners;
  isLoading: boolean;
  onSave: (result: Record<string, ResultValue>, runs: string) => void;
}

const ResultModal: React.FC<ResultModalProps> = ({
  open,
  onClose,
  eventName,
  marketName,
  marketType,
  runners,
  onSave,
  isLoading,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { modalRef, teams, results, runs, setRuns, handleSelect, handleSave, confirmSave } =
    useResultModal({
      open,
      marketType,
      marketName,
      runners,
      onClose,
      onSave,
    });
  React.useEffect(() => {
    if (!open) {
      setIsConfirmOpen(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSaveClick = () => {
    if (handleSave()) {
      setIsConfirmOpen(true);
    }
  };

  const handleConfirmSave = () => {
    setIsConfirmOpen(false);
    confirmSave();
  };
  const normalizedMarketName = marketName?.toLowerCase().replace(/[^a-z]/g, '');
  const isOddEven = normalizedMarketName === 'oddeven' || normalizedMarketName === 'oddseven';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 text-gray-800 flex items-center justify-center z-[99]">
      <div ref={modalRef} className=" bg-white p-5 rounded-lg shadow-xl w-[95%] max-w-lg">
        <div className="flex justify-between gap-x-2">
          <h2 className="font-semibold text-lg mb-4">Event: {eventName}</h2>
          <button onClick={onClose} className="self-start bg-primary-light rounded-full p-1 shadow">
            <X size={18} />
          </button>
        </div>

        {teams.length === 0 ? (
          <div className="w-full text-center py-4 text-muted-foreground">No Runners Available</div>
        ) : (
          <>
            <div className="max-h-[400px] overflow-y-auto border border-black/20 rounded-md p-4 space-y-4 bg-primary-light/10">
              {teams.map((team) => (
                <div key={team}>
                  <p className="font-semibold mb-1">{team}</p>

                  {isOddEven ? (
                    <Select
                      onValueChange={(value) =>
                        handleSelect(team, value as 'ODD' | 'EVEN' | 'None')
                      }
                      value={results[team]?.status ?? ''}
                    >
                      <SelectTrigger
                        className={cn(
                          'w-full font-medium',
                          results[team]?.status === 'ODD' && 'bg-primary/80 text-white',
                          results[team]?.status === 'EVEN' && 'bg-red-200'
                        )}
                      >
                        <SelectValue placeholder="Choose option" />
                      </SelectTrigger>

                      <SelectContent className="z-[100]">
                        <SelectItem value="ODD">ODD</SelectItem>
                        <SelectItem value="EVEN">EVEN</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : marketType === 'fancy' && marketName?.toLowerCase() !== 'session' ? (
                    <Select
                      onValueChange={(value) =>
                        handleSelect(team, value as 'Winner' | 'Looser' | 'None')
                      }
                      value={results[team]?.status ?? ''}
                    >
                      <SelectTrigger
                        className={cn(
                          'w-full font-medium',
                          results[team]?.status === 'Winner' && 'bg-primary/80 text-white',
                          results[team]?.status === 'Looser' && 'bg-red-200'
                        )}
                      >
                        <SelectValue placeholder="Choose option" />
                      </SelectTrigger>

                      <SelectContent className="z-[100]">
                        <SelectItem value="Winner">Winner</SelectItem>
                        <SelectItem value="Looser">Looser</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : marketType === 'fancy' ? (
                    <input
                      type="text"
                      className="py-1.5 px-2 w-full border rounded"
                      value={runs}
                      maxLength={4}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          setRuns(value);
                        }
                      }}
                    />
                  ) : (
                    <Select
                      onValueChange={(value) =>
                        handleSelect(team, value as 'Winner' | 'Looser' | 'None')
                      }
                      value={results[team]?.status ?? ''}
                    >
                      <SelectTrigger
                        className={cn(
                          'w-full font-medium',
                          results[team]?.status === 'Winner' && 'bg-primary/80 text-white',
                          results[team]?.status === 'Looser' && 'bg-red-200',
                          results[team]?.status === 'None' && 'bg-gray-500 text-gray-100'
                        )}
                      >
                        <SelectValue placeholder="Choose option" />
                      </SelectTrigger>

                      <SelectContent className="z-[100]">
                        <SelectItem value="Winner">Winner</SelectItem>
                        <SelectItem value="Looser">Looser</SelectItem>
                        <SelectItem value="None">None</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-4">
              <Button
                disabled={isLoading}
                className="text-white bg-btn-gradient w-[100px] "
                onClick={handleSaveClick}
              >
                {isLoading ? (
                  <div className="flex items-center gap-x-2">
                    <span className=" inline-block animate-spin">
                      <Loader2 className="w-5" />
                    </span>
                    Saving...
                  </div>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      <ResultConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmSave}
        isLoading={isLoading}
      />
    </div>,
    document.body
  );
};

export default ResultModal;
